import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  browserlessService,
  parseHtmlMetadata,
  parseDuckDuckGoResults,
} from '../../../../../../utils/services/browserlessService';

export const SYSTEM_EVENT_TYPE = 'system_event';

const isValidUrlStrict =(urlString) => {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Turn host/path strings (no scheme) into https URLs when parseable. */
const normalizeIncidentUrl = (raw) => {
  const t = (raw || '').trim();
  if (!t) return '';
  if (isValidUrlStrict(t)) return t;
  if (/^[\w.-]+\.[a-z]{2,}([/:?#][^\s]*)?$/i.test(t)) {
    const withScheme = `https://${t}`;
    if (isValidUrlStrict(withScheme)) return withScheme;
  }
  return '';
};

export const fetchImageData = createAsyncThunk(
  'browserLessImage/fetchImageData',
  async ({ type_cd, score, app_package_name, incidentname_tx, incident_id, event_title_tx, event_extra_package_name, event_app_title_tx }) => {

    // System events: no URL inspector requests
    if (type_cd === SYSTEM_EVENT_TYPE) {
      return { imageStatus: 'Not Found', imageBinaryData: '', title: 'No title found', description: 'No description found' };
    }

    // High score: do not scrape
    const highScoreThreshold = 900;
    if (score != null && Number(score) > highScoreThreshold) {
      return { imageStatus: 'Blocked', imageBinaryData: '', title: '', description: '' };
    }

    let searchtext = '';
    let imageStatus = '';
    let imageBinaryData = '';
    let title = '';
    let description = '';

    // URL-centric incident types must use the page URL (incidentname_tx), not Play Store,
    // even when the app has a package name — otherwise we screenshot/search the wrong thing.
    const incidentTypesPreferUrl = ['visited_url', 'search_result_url'];
    const incidentRaw = (incidentname_tx || '').trim();
    const incidentLower = incidentRaw.toLowerCase();
    const normalizedIncidentUrl = normalizeIncidentUrl(incidentRaw);
    const isScreenshotOnlyIncidentName = incidentLower.includes('screenshot only incident');
    const isGoogleIncidentName = incidentLower.includes('google');

    const eventTitleRaw = (event_title_tx || '').trim();
    if (isGoogleIncidentName && eventTitleRaw) {
      // Only use the event's title_tx — never fall back to the incident name
      // (which contains "Google") as that would search the word "google" itself.
      searchtext = eventTitleRaw;
    }

    if (!searchtext && isScreenshotOnlyIncidentName) {
      const GOOGLE_SEARCH_BOX = 'com.google.android.googlequicksearchbox';
      const extraPackage = (event_extra_package_name || '').trim();

      if (extraPackage === GOOGLE_SEARCH_BOX) {
        // Google Quick Search Box: search the event's title_tx on DuckDuckGo.
        searchtext = (event_title_tx || '').trim() || incidentRaw;
      } else if (extraPackage) {
        // Real app package: go straight to Play Store.
        searchtext = `https://play.google.com/store/apps/details?id=${encodeURIComponent(extraPackage)}`;
      } else {
        // No extra package name: search event.app_title_tx on DuckDuckGo.
        searchtext = (event_app_title_tx || '').trim() || incidentRaw;
      }
    }

    if (!searchtext && incidentTypesPreferUrl.includes(type_cd)) {
      searchtext = (normalizedIncidentUrl && isValidUrlStrict(normalizedIncidentUrl))
        ? normalizedIncidentUrl
        : incidentRaw;
    }

    if (!searchtext) {
      searchtext = app_package_name?.trim()
        ? `https://play.google.com/store/apps/details?id=${encodeURIComponent(app_package_name.trim())}`
        : incidentRaw;
    }

    // Fallback to DuckDuckGo HTML search if not a direct URL.
    if (!isValidUrlStrict(searchtext) && !searchtext.includes('play.google.com')) {
      searchtext = `https://duckduckgo.com/html/?q=${encodeURIComponent(searchtext)}`;
    }

    const validTypes = ['visited_url', 'app_activity', 'app_report', 'search_result_url', 'screenshot_only'];

    if (validTypes.includes(type_cd)) {
      const isDuckSearch = /^https?:\/\/duckduckgo\.com\/html\/\?q=/.test(searchtext);

      // ---------------- CONTENT / SCRAPING ----------------
      try {
        if (isDuckSearch) {
          const html = await browserlessService.getContent(searchtext);
          const parsed = parseDuckDuckGoResults(html);
          title = parsed.title || 'No title found';
          description = parsed.description || 'No description found';
        } else {
          const html = await browserlessService.getContent(searchtext);
          const parsed = parseHtmlMetadata(html);
          title = parsed.title || '';
          description = parsed.description || '';

          if (!title && !description) {
            title = 'No title found';
            description = 'No description found';
          }
        }
      } catch {
        if (isDuckSearch) {
          title = 'No title found';
          description = 'No description found';
        } else {
          title = 'Error fetching title';
          description = 'Error fetching description';
        }
      }

      // ---------------- SCREENSHOT ----------------
      if (score <= 900) {
        try {
          imageBinaryData = await browserlessService.takeScreenshot(searchtext);
          imageStatus = imageBinaryData ? 'Found' : 'Not Found';
        } catch {
          imageStatus = 'Not Found';
        }
      } else {
        imageStatus = 'Blocked';
        imageBinaryData = '';
      }
    } else {
      imageStatus = 'Not Found';
      title = 'No Image Available';
      description = 'No search text returned...';
    }

    return { imageStatus, imageBinaryData, title, description };
  },
  {
    condition: (arg, { getState }) => {
      const { isLoading } = getState().browserLessImage;
      return !isLoading;
    },
  }
);
